import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, ImageIcon, Package, ChevronRight, Truck, Handshake, Check, PackageCheck, Link2, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { API, api } from "@/lib/api";
import {
  updateOrder,
  updateOrderItem,
  listFinishedPhotos,
  uploadUserMediaFile,
  deliveryLabel,
  getOrderPaymentsSummary,
  getOrderShareLinkStatus,
  createOrRotateOrderShareLink,
  revokeOrderShareLink,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";
import { PaymentsDrawer } from "@/components/masterOrder/PaymentsDrawer";

/* -------- kg / g conversion helpers ---------------------------------------
 * Confectioners type in kg («2,65 кг»). Backend stores strictly positive
 * Decimal grams. We convert client-side so the UI never exposes «grams».
 * Accept both comma and dot as decimal separator. Round to 1 gram.
 * -------------------------------------------------------------------------- */
const kgInputToGrams = (raw) => {
  if (raw === "" || raw == null) return null;
  const norm = String(raw).replace(",", ".").trim();
  if (!norm) return null;
  const n = Number(norm);
  if (!Number.isFinite(n) || n <= 0) return "INVALID";
  return Math.round(n * 1000);
};
const gramsToKgString = (grams) => {
  if (grams == null || grams === "") return "";
  const n = Number(grams);
  if (!Number.isFinite(n) || n <= 0) return "";
  // Show up to 3 decimals, drop trailing zeros
  const kg = n / 1000;
  return String(kg.toFixed(3)).replace(/\.?0+$/, "").replace(".", ",");
};

/* -------- absolute URL for platform media -------------------------------- */
const mediaUrl = (relOrAbs) => {
  if (!relOrAbs) return "";
  if (/^https?:/i.test(relOrAbs)) return relOrAbs;
  // Media file_url comes back like "/api/media/files/{gid}". API constant
  // already includes "/api", so strip the "/api" prefix to avoid doubling.
  const base = API.replace(/\/api\/?$/, "");
  return `${base}${relOrAbs}`;
};

/* -------- Authed media -> blob: URL for owner-side <img> ------------------
 * Owner-scoped media (/api/media/files/{gid}) is gated by Bearer or cookie.
 * `<img src>` requests can't carry an Authorization header, and iOS Safari
 * unreliably sends the SameSite=Lax cookie for image loads on the preview
 * host — hence the broken-image icon on iPhone. We sidestep the entire
 * cookie-auth problem by fetching the bytes via authed axios and swapping
 * the result to a blob: URL. Works identically on desktop and mobile. */
function useAuthedMediaBlob(fileUrl) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!fileUrl) { setBlobUrl(null); return; }
    // Non-owner (already absolute http) URLs stay as-is: no auth needed.
    if (/^https?:/i.test(fileUrl)) { setBlobUrl(fileUrl); return; }
    let cancelled = false;
    let created = null;
    // Strip the leading "/api" — axios baseURL already ends in "/api".
    const rel = fileUrl.replace(/^\/api\/?/, "/");
    api.get(rel, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        created = URL.createObjectURL(data);
        setBlobUrl(created);
      })
      .catch(() => { if (!cancelled) setBlobUrl(null); });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [fileUrl]);
  return blobUrl;
}

/* -------- Human "22 августа · 14:35" from ISO string ------------------- */
const RU_MONTHS_GEN = [
  "января","февраля","марта","апреля","мая","июня",
  "июля","августа","сентября","октября","ноября","декабря",
];
const fmtHandedOverAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDate();
  const mon = RU_MONTHS_GEN[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} · ${hh}:${mm}`;
};

/* -------- Money formatter for minor units -----------------------------
 * Backend delivers integer minor units (e.g. cents). We render "86,00 EUR"
 * / "8 900,00 RUB" — RU-style comma decimal, non-breaking space thousand
 * separator, ISO code stays visible (never replaced with a symbol). Locale
 * is fixed to avoid the browser silently swapping "EUR" for "€".
 * ---------------------------------------------------------------------- */
const NBSP = "\u00A0";
const fmtMoneyMinor = (minor, currencyCode) => {
  if (minor == null || currencyCode == null) return "—";
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, "0");
  // Space-group thousands: 8 900
  const majorStr = String(major).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${negative ? "−" : ""}${majorStr},${cents}${NBSP}${currencyCode}`;
};

/* -------- Compact summary section --------------------------------------- */

/**
 * Read-only payment summary block (Sprint Payment-2).
 *
 * Backend is source of truth: this component only formats.
 * States:
 *   summary === null   → still loading  (skip render — non-blocking)
 *   summary === false  → fetch failed   (small muted line, other blocks fine)
 *   summary object     → render rows depending on `is_total_reliable`
 *                        and the net_paid / remaining / overpayment shape.
 */
function PaymentSummary({ summary }) {
  // Loading — do not reserve space, do not flash placeholder.
  if (summary === null) return null;

  // Fetch failure — never crash the parent, show a discreet notice.
  if (summary === false) {
    return (
      <div className="mt-3 rounded-lg p-2.5 text-[11.5px]"
           style={{ background: "#FBF3E4", color: "#8B6F4B",
                    boxShadow: "inset 0 0 0 1px #EFE4D0" }}
           data-testid="mo-finished-pay-summary-error">
        Не удалось загрузить сводку оплат
      </div>
    );
  }

  const {
    currency_code,
    sale_total_minor,
    net_paid_minor,
    remaining_minor,
    overpayment_minor,
    is_total_reliable,
  } = summary;

  const paidLine = fmtMoneyMinor(net_paid_minor, currency_code);

  // Unreliable total → never display a misleading remaining balance.
  if (!is_total_reliable) {
    return (
      <div className="mt-3 rounded-lg p-3 space-y-1.5"
           style={{ background: "#FBF3E4", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
           data-testid="mo-finished-pay-summary">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px]" style={{ color: "#5B4326" }}>Оплачено</span>
          <span className="text-[13px] tabular-nums whitespace-nowrap"
                style={{ color: "#2A1D14" }}
                data-testid="mo-finished-pay-paid">
            {paidLine}
          </span>
        </div>
        <div className="text-[12px] leading-snug pt-0.5"
             style={{ color: "#8B6F4B" }}
             data-testid="mo-finished-pay-partial-total-note">
          <div style={{ color: "#5B4326" }}>Итоговая стоимость ещё рассчитывается</div>
          <div>Не для всех позиций заказа готов расчёт.</div>
        </div>
      </div>
    );
  }

  const totalLine = fmtMoneyMinor(sale_total_minor, currency_code);
  const isFullyPaid = remaining_minor === 0 && (overpayment_minor || 0) === 0;
  const hasOverpayment = (overpayment_minor || 0) > 0;

  return (
    <div className="mt-3 rounded-lg p-3 space-y-1.5"
         style={{ background: "#FBF3E4", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-finished-pay-summary">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px]" style={{ color: "#5B4326" }}>Стоимость заказа</span>
        <span className="text-[13px] tabular-nums whitespace-nowrap"
              style={{ color: "#2A1D14" }}
              data-testid="mo-finished-pay-total">
          {totalLine}
        </span>
      </div>

      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px]" style={{ color: "#5B4326" }}>Оплачено</span>
        <span className="text-[13px] tabular-nums whitespace-nowrap"
              style={{ color: "#2A1D14" }}
              data-testid="mo-finished-pay-paid">
          {paidLine}
        </span>
      </div>

      {isFullyPaid ? (
        <div className="text-[12px] pt-0.5"
             style={{ color: "#4A3728" }}
             data-testid="mo-finished-pay-fully-paid">
          Оплачено полностью
        </div>
      ) : hasOverpayment ? (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px]" style={{ color: "#5B4326" }}>Переплата</span>
          <span className="text-[13px] tabular-nums whitespace-nowrap"
                style={{ color: "#4A3728" }}
                data-testid="mo-finished-pay-overpayment">
            {fmtMoneyMinor(overpayment_minor, currency_code)}
          </span>
        </div>
      ) : (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px]" style={{ color: "#5B4326" }}>К&nbsp;доплате</span>
          <span className="text-[13px] tabular-nums whitespace-nowrap"
                style={{ color: "#2A1D14" }}
                data-testid="mo-finished-pay-remaining">
            {fmtMoneyMinor(remaining_minor, currency_code)}
          </span>
        </div>
      )}
    </div>
  );
}

/* -------- Photo widget shared by owner drawer + owner summary ------------
 * Renders the FULL original image using its natural aspect ratio (no
 * cropping, no fixed short container). Loaded via authed axios → blob:
 * URL so iOS Safari never has to rely on cookie auth for <img> requests. */
function AuthedPhoto({ fileUrl, testId, containerMaxH = 520 }) {
  const blobUrl = useAuthedMediaBlob(fileUrl);
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [fileUrl]);
  return (
    <div className="w-full bg-[#F7F1E7] rounded-md overflow-hidden"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
      {blobUrl && !errored ? (
        <img src={blobUrl}
             alt="Готовое изделие"
             onError={() => setErrored(true)}
             className="block w-full h-auto object-contain"
             style={{ maxHeight: containerMaxH }}
             data-testid={testId} />
      ) : (
        <div className="w-full grid place-items-center"
             style={{ minHeight: 180, color: "#B9A692" }}>
          <ImageIcon size={22} />
        </div>
      )}
    </div>
  );
}


export function FinishedOrderBlock({ order, items, ptMap, canWrite, onSaved }) {
  const [photos, setPhotos] = useState([]); // [{id, file_url}]
  const [paySummary, setPaySummary] = useState(null); // null=loading, false=error, obj=ok
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paymentsDrawerOpen, setPaymentsDrawerOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const load = async () => {
    try {
      const res = await listFinishedPhotos(order.id);
      setPhotos(res);
    } catch (e) { /* silent — non-blocking */ }
    // Payment summary — independent request; a failure must NOT break the block.
    try {
      const s = await getOrderPaymentsSummary(order.id);
      setPaySummary(s);
    } catch (e) {
      setPaySummary(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [order.id, order.payment_status, order.updated_at]);

  const isReady = order.business_status === "ready";
  const isDelivered = order.business_status === "delivered";
  const handedAtLabel = fmtHandedOverAt(order.handed_over_at);

  const readyLabel =
    isDelivered ? "Выдан" :
    isReady ? "Готов к выдаче" :
    null;

  const hasAnyData =
    (order.finished_photo_media_ids?.length ?? 0) > 0 ||
    (order.handoff_message && order.handoff_message.trim()) ||
    (order.storage_instructions && order.storage_instructions.trim()) ||
    items.some((it) => it.actual_weight_grams != null);

  /* --------- Ready / Delivered actions (Step 3) ------------------------- */
  const markReady = async () => {
    if (!canWrite || transitioning) return;
    if (!window.confirm("Отметить заказ как готовый к выдаче?")) return;
    setTransitioning(true);
    try {
      const updated = await updateOrder(order.id, { business_status: "ready" });
      toast.success("Заказ отмечен как готовый к выдаче");
      await onSaved(updated);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTransitioning(false); }
  };
  const markDelivered = async () => {
    if (!canWrite || transitioning) return;
    if (!window.confirm("Отметить заказ как выданный?")) return;
    setTransitioning(true);
    try {
      // business_status + handed_over_at are written atomically in ONE PATCH.
      const updated = await updateOrder(order.id, {
        business_status: "delivered",
        handed_over_at: new Date().toISOString(),
      });
      toast.success("Заказ отмечен как выданный");
      await onSaved(updated);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTransitioning(false); }
  };

  return (
    <section className="rounded-xl bg-white p-5"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-order-finished-block">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-[17px]"
             style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Готовый заказ
        </div>
        {readyLabel && (
          <span className="text-[11px] px-2.5 py-0.5 rounded-full whitespace-nowrap"
                style={{ background: "#EADFCF", color: "#4A3728" }}
                data-testid="mo-finished-status-label">
            {readyLabel}
          </span>
        )}
      </div>

      {/* Delivered timestamp — Step 3 */}
      {isDelivered && handedAtLabel && (
        <div className="mb-3 text-[12px]"
             style={{ color: "#5B4326" }}
             data-testid="mo-finished-handed-over-at">
          <span style={{ color: "#8B6F4B" }}>Выдан </span>{handedAtLabel}
        </div>
      )}

      {!hasAnyData ? (
        <div className="text-[12.5px] leading-relaxed" style={{ color: "#8B6F4B" }}
             data-testid="mo-finished-empty">
          Подготовьте данные готового заказа: фото, фактический вес, сообщение клиенту, условия хранения.
        </div>
      ) : (
        <div className="space-y-3">
          {photos.length > 0 && (
            <div data-testid="mo-finished-summary-photos">
              <AuthedPhoto fileUrl={photos[0].file_url}
                           testId="mo-finished-summary-photo-img"
                           containerMaxH={520} />
            </div>
          )}

          {items.some((it) => it.actual_weight_grams != null) && (
            <div className="text-[11.5px]" style={{ color: "#5B4326" }}
                 data-testid="mo-finished-summary-weights">
              <div className="text-[10.5px] tracking-[0.14em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>
                Фактический вес
              </div>
              {items.map((it) => it.actual_weight_grams != null ? (
                <div key={it.id} className="flex justify-between gap-2">
                  <span className="truncate">{ptMap[it.product_type_id]?.name || "Позиция"}</span>
                  <span className="tabular-nums whitespace-nowrap">
                    {gramsToKgString(it.actual_weight_grams)} кг
                  </span>
                </div>
              ) : null)}
            </div>
          )}

          {order.handoff_message && (
            <div className="text-[12px] leading-relaxed" style={{ color: "#2A1D14" }}
                 data-testid="mo-finished-summary-message">
              <div className="text-[10.5px] tracking-[0.14em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>
                Сообщение клиенту
              </div>
              <div className="line-clamp-2">{order.handoff_message}</div>
            </div>
          )}

          {order.storage_instructions && (
            <div className="text-[12px] leading-relaxed" style={{ color: "#2A1D14" }}
                 data-testid="mo-finished-summary-storage">
              <div className="text-[10.5px] tracking-[0.14em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>
                Хранение и перевозка
              </div>
              <div className="line-clamp-2">{order.storage_instructions}</div>
            </div>
          )}
        </div>
      )}

      {/* ---- Payment summary (Sprint Payment-2, read-only) ---------------- */}
      <PaymentSummary summary={paySummary} />
      {paySummary && paySummary !== false && (
        <div className="mt-2 flex justify-end">
          <button onClick={() => setPaymentsDrawerOpen(true)}
                  className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                  style={{ background: "#F7F1E7", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-finished-open-payments">
            Оплаты
          </button>
        </div>
      )}

      {/* ---- Client share-link (Sprint Client-Link-3) --------------------- */}
      <ShareLinkPanel orderId={order.id} canWrite={canWrite} />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setDrawerOpen(true)}
                className="h-9 px-3.5 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                style={{ background: canWrite ? "#F7F1E7" : "#F7F1E7",
                         color: "#4A3728",
                         boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-finished-open">
          {canWrite ? (hasAnyData ? "Открыть" : "Заполнить") : "Посмотреть"}
          <ChevronRight size={12} />
        </button>

        {/* Step 3 actions — writable only, no rollback */}
        {canWrite && !isReady && !isDelivered && (
          <button onClick={markReady}
                  disabled={transitioning}
                  className="h-9 px-3.5 rounded-xl text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-finished-mark-ready">
            <Check size={13} strokeWidth={2} />
            Готов к выдаче
          </button>
        )}
        {canWrite && isReady && (
          <button onClick={markDelivered}
                  disabled={transitioning}
                  className="h-9 px-3.5 rounded-xl text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-finished-mark-delivered">
            <PackageCheck size={13} strokeWidth={2} />
            Заказ выдан
          </button>
        )}
      </div>

      {drawerOpen && (
        <FinishedOrderDrawer
          order={order}
          items={items}
          ptMap={ptMap}
          photos={photos}
          canWrite={canWrite}
          onClose={() => setDrawerOpen(false)}
          onSaved={async (updatedOrder) => {
            await load();
            onSaved(updatedOrder);
          }}
        />
      )}

      {paymentsDrawerOpen && (
        <PaymentsDrawer
          order={order}
          canWrite={canWrite}
          onClose={() => setPaymentsDrawerOpen(false)}
          onOrderChanged={async () => {
            // Reload block (photos + summary) + let parent refresh Order for status badge
            await load();
            onSaved?.(null);
          }}
        />
      )}
    </section>
  );
}

/* -------- Edit drawer ---------------------------------------------------- */
function FinishedOrderDrawer({ order, items, ptMap, photos, canWrite, onClose, onSaved }) {
  const [mediaIds, setMediaIds] = useState(order.finished_photo_media_ids || []);
  const [mediaCache, setMediaCache] = useState(() => {
    const m = {};
    for (const p of photos) m[p.id] = p.file_url;
    return m;
  });
  const [handoffMessage, setHandoffMessage] = useState(order.handoff_message || "");
  const [storageInstructions, setStorageInstructions] = useState(order.storage_instructions || "");
  // per-item weight strings in kg (as user typed)
  const [weightKgByItem, setWeightKgByItem] = useState(() => {
    const m = {};
    for (const it of items) m[it.id] = gramsToKgString(it.actual_weight_grams);
    return m;
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  const orderedPhotos = useMemo(
    () => mediaIds.map((id) => ({ id, file_url: mediaCache[id] || "" })),
    [mediaIds, mediaCache]
  );

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Sprint «Фото готового изделия»: only 1 photo is allowed. Whether the
    // user is uploading for the first time or replacing an existing one,
    // we take the first picked file and REPLACE the current mediaIds list
    // (old id is dropped; Media record itself is not deleted server-side —
    // that's a platform-media concern outside this sprint).
    const f = files[0];
    const type = (f.type || "").toLowerCase();
    const ext = (f.name || "").toLowerCase().split(".").pop();
    // 1) Explicit HEIC/HEIF branch — real iPhone uploads may arrive as HEIC
    //    when the user disables «Most compatible» in Photos. The platform
    //    Media backend can only serve JPG/PNG/WEBP, so accepting a HEIC and
    //    silently 415-rejecting it later leaves the user confused. Reject
    //    up-front with a clear message.
    const isHeic =
      type === "image/heic" || type === "image/heif" ||
      ext === "heic" || ext === "heif";
    if (isHeic) {
      toast.error("Этот формат фото пока не поддерживается. Выберите JPG, PNG или WEBP.");
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    // 2) Allowlist + size cap.
    const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const MAX_BYTES = 5 * 1024 * 1024;
    const extAllowed = ["jpg", "jpeg", "png", "webp"].includes(ext);
    if (!(ALLOWED.includes(type) || extAllowed)) {
      toast.error("Формат не поддерживается. Разрешены JPG, PNG или WEBP.");
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Файл слишком большой. Максимум 5 МБ.");
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    setUploading(true);
    try {
      try {
        const media = await uploadUserMediaFile(f);
        setMediaIds([media.id]);
        setMediaCache((cur) => ({ ...cur, [media.id]: media.file_url }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };
  const removePhoto = () => {
    setMediaIds([]);
  };

  const handleSave = async () => {
    if (!canWrite) return;
    if (handoffMessage.length > 500) {
      toast.error("Сообщение клиенту не должно быть длиннее 500 символов");
      return;
    }
    if (storageInstructions.length > 1000) {
      toast.error("Условия хранения не должны быть длиннее 1000 символов");
      return;
    }
    // Validate weight inputs
    const weightPatches = []; // [{ id, grams|null }]
    for (const it of items) {
      const raw = weightKgByItem[it.id];
      const g = kgInputToGrams(raw);
      if (g === "INVALID") {
        toast.error(`Вес позиции «${ptMap[it.product_type_id]?.name || "Позиция"}» должен быть больше 0`);
        return;
      }
      // Only PATCH if value actually changed
      const current = it.actual_weight_grams == null ? null : Number(it.actual_weight_grams);
      if ((g ?? null) !== current) weightPatches.push({ id: it.id, grams: g });
    }

    setSaving(true);
    try {
      const orderPayload = {
        finished_photo_media_ids: mediaIds,
        handoff_message: handoffMessage.trim() ? handoffMessage : null,
        storage_instructions: storageInstructions.trim() ? storageInstructions : null,
      };
      const updated = await updateOrder(order.id, orderPayload);
      for (const wp of weightPatches) {
        await updateOrderItem(wp.id, { actual_weight_grams: wp.grams });
      }
      toast.success("Готовый заказ сохранён");
      await onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deliverySummary = order.delivery ? {
    type: order.delivery.type,
    address: order.delivery.address,
    date: order.delivery.date,
    window: order.delivery.time_window,
  } : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-finished-drawer">
      <div className="w-full md:max-w-xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="flex items-start justify-between mb-1">
          <div className="text-[19px]"
               style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Готовый заказ
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-finished-drawer-close">
            <X size={13} />
          </button>
        </div>
        <div className="text-[12px] mb-4" style={{ color: "#8B6F4B" }}>
          Что нужно для выдачи: фото, вес, сообщение клиенту, условия хранения.
        </div>

        {/* 1. Photos — single photo per Sprint «Фото готового изделия» */}
        <div className="mb-4">
          <div className="text-[10.5px] tracking-[0.14em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Фото готового изделия
          </div>
          <div data-testid="mo-finished-photos">
            {orderedPhotos.length > 0 ? (
              <div className="rounded-lg overflow-hidden bg-[#F7F1E7]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-finished-photo">
                {orderedPhotos[0].file_url ? (
                  <AuthedPhoto fileUrl={orderedPhotos[0].file_url}
                               testId="mo-finished-photo-img"
                               containerMaxH={520} />
                ) : (
                  <div className="w-full h-48 grid place-items-center" style={{ color: "#B9A692" }}>
                    <ImageIcon size={26} />
                  </div>
                )}
                {canWrite && (
                  <div className="p-2 flex flex-wrap gap-2 border-t border-[#EFE4D0]">
                    <button onClick={() => fileInput.current?.click()}
                            disabled={uploading}
                            className="h-9 px-3 rounded-xl text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                            style={{ background: "#F7F1E7", color: "#4A3728",
                                     boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                            data-testid="mo-finished-photo-replace">
                      <RefreshCw size={12} /> {uploading ? "Загружаем…" : "Заменить фото"}
                    </button>
                    <button onClick={removePhoto}
                            disabled={uploading}
                            className="h-9 px-3 rounded-xl text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                            style={{ background: "transparent", color: "#B26B4A",
                                     boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                            data-testid="mo-finished-photo-remove">
                      <X size={12} /> Удалить фото
                    </button>
                  </div>
                )}
              </div>
            ) : (
              canWrite && (
                <button onClick={() => fileInput.current?.click()}
                        disabled={uploading}
                        className="w-full h-32 rounded-lg grid place-items-center text-[13px] disabled:opacity-60"
                        style={{ background: "#F7F1E7", color: "#4A3728",
                                 boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-finished-photo-add">
                  {uploading ? "Загружаем…" : (
                    <div className="flex flex-col items-center gap-1">
                      <Upload size={18} />
                      <span>Загрузить фото</span>
                    </div>
                  )}
                </button>
              )
            )}
            {canWrite && (
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                     className="hidden" onChange={onFilesPicked}
                     data-testid="mo-finished-photo-input" />
            )}
          </div>
        </div>

        {/* 2. Actual weight per item */}
        {items.length > 0 && (
          <div className="mb-4">
            <div className="text-[10.5px] tracking-[0.14em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
              Фактический вес по позициям
            </div>
            <div className="space-y-2" data-testid="mo-finished-weights">
              {items.map((it) => {
                const ptName = ptMap[it.product_type_id]?.name || "Позиция";
                const val = weightKgByItem[it.id] || "";
                const gramsPreview = kgInputToGrams(val);
                const showGrams = typeof gramsPreview === "number";
                return (
                  <div key={it.id}
                       className="rounded-lg bg-white p-3 flex items-center gap-3"
                       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                       data-testid="mo-finished-weight-row">
                    <div className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                         style={{ background: "#F7F1E7", color: "#8B6F4B" }}>
                      <Package size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] truncate" style={{ color: "#2A1D14" }}>
                        {ptName}
                      </div>
                      <div className="text-[11px]" style={{ color: "#8B6F4B" }}>
                        {it.quantity > 1 ? `${it.quantity} шт.` : "1 шт."}
                        {showGrams && ` · ${gramsPreview} г`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={val}
                        readOnly={!canWrite}
                        onChange={(e) => setWeightKgByItem((cur) => ({ ...cur, [it.id]: e.target.value }))}
                        placeholder="—"
                        className="w-20 h-9 px-2 rounded-md bg-white text-[13px] outline-none text-right tabular-nums"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                        data-testid="mo-finished-weight-input"
                      />
                      <span className="text-[12px]" style={{ color: "#5B4326" }}>кг</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. Handoff message */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>
              Сообщение клиенту
            </div>
            <span className="text-[10.5px]" style={{ color: "#B9A692" }}>
              {handoffMessage.length}/500
            </span>
          </div>
          <textarea
            value={handoffMessage}
            onChange={(e) => setHandoffMessage(e.target.value.slice(0, 500))}
            readOnly={!canWrite}
            rows={3}
            placeholder="Например: спасибо за заказ! Свечи в комплекте, желаем чудесного праздника."
            className="w-full p-3 rounded-lg bg-white text-[13px] outline-none resize-none"
            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
            data-testid="mo-finished-message"
          />
        </div>

        {/* 4. Storage / transport */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>
              Хранение и перевозка
            </div>
            <span className="text-[10.5px]" style={{ color: "#B9A692" }}>
              {storageInstructions.length}/1000
            </span>
          </div>
          <textarea
            value={storageInstructions}
            onChange={(e) => setStorageInstructions(e.target.value.slice(0, 1000))}
            readOnly={!canWrite}
            rows={4}
            placeholder="Например: хранить в холодильнике при +4°C, перевозить на ровной поверхности, не оставлять в машине на солнце."
            className="w-full p-3 rounded-lg bg-white text-[13px] outline-none resize-none"
            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
            data-testid="mo-finished-storage"
          />
        </div>

        {/* 5. Compact handoff summary (read-only reuse of Получение) */}
        <div className="mb-4">
          <div className="text-[10.5px] tracking-[0.14em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Получение
          </div>
          <div className="rounded-lg bg-white p-3 text-[12.5px]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid="mo-finished-delivery-summary">
            {deliverySummary ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5" style={{ color: "#4A3728" }}>
                  {deliverySummary.type === "pickup"
                    ? <Handshake size={12} /> : <Truck size={12} />}
                  {deliveryLabel(deliverySummary.type)}
                </div>
                {deliverySummary.address && <div>{deliverySummary.address}</div>}
                {(deliverySummary.date || deliverySummary.window) && (
                  <div style={{ color: "#5B4326" }}>
                    {[deliverySummary.date, deliverySummary.window].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: "#8B6F4B" }}>
                Способ получения не указан. Задайте в блоке «Получение».
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-xl text-[13px]"
                  style={{ background: "transparent", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-finished-drawer-cancel">
            {canWrite ? "Отмена" : "Закрыть"}
          </button>
          {canWrite && (
            <button onClick={handleSave} disabled={saving}
                    className="h-10 px-5 rounded-xl text-[13px] disabled:opacity-60"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-finished-drawer-save">
              {saving ? "Сохраняем..." : "Сохранить"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* -------- Client share-link panel (Sprint Client-Link-3) -----------------
 * Backend contract in one place:
 *   - POST /orders/{id}/share-link returns the raw token ONCE. We cache it
 *     in local state for the lifetime of this component so Copy / Open
 *     remain usable until the user leaves the page.
 *   - GET  /orders/{id}/share-link returns metadata only (no raw token).
 *     If the panel loads after a page refresh, we can see the link is
 *     active (last4) but we cannot rebuild the URL — the user must rotate
 *     to get a fresh raw token (this is documented, not a bug).
 *   - Rotate is the same POST endpoint; server atomically revokes the
 *     previous token, so the "old link stops working" guarantee is enforced
 *     server-side and does not need extra client work.
 * Access:
 *   - All write actions gated by `canWrite` (owner-side rule from parent).
 * ------------------------------------------------------------------------ */
function ShareLinkPanel({ orderId, canWrite }) {
  const [status, setStatus] = useState(null); // { exists, active, token_last4, ... }
  const [rawToken, setRawToken] = useState(null); // available ONLY after POST
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getOrderShareLinkStatus(orderId);
        if (!cancelled) setStatus(s);
      } catch (e) {
        if (!cancelled) setStatus({ exists: false, active: false });
      }
    })();
    return () => { cancelled = true; };
    // Refetch when a new order is loaded into this block; rotation itself
    // updates the local state directly, so we don't refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const publicUrl = useMemo(() => {
    if (!rawToken) return null;
    if (typeof window === "undefined") return `/order/${rawToken}`;
    return `${window.location.origin}/order/${rawToken}`;
  }, [rawToken]);

  const create = async () => {
    if (!canWrite || busy) return;
    setBusy(true);
    try {
      const res = await createOrRotateOrderShareLink(orderId);
      setRawToken(res.raw_token);
      setStatus({
        exists: true, active: true, id: res.id,
        token_last4: res.token_last4, created_at: res.created_at,
      });
      toast.success("Ссылка создана");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!canWrite || busy) return;
    if (!window.confirm("Перевыпустить ссылку? Предыдущая ссылка перестанет работать.")) return;
    setBusy(true);
    try {
      const res = await createOrRotateOrderShareLink(orderId);
      setRawToken(res.raw_token);
      setStatus({
        exists: true, active: true, id: res.id,
        token_last4: res.token_last4, created_at: res.created_at,
      });
      toast.success("Ссылка перевыпущена");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!canWrite || busy || !status?.active) return;
    if (!window.confirm("Отозвать ссылку? Клиент больше не сможет открыть её.")) return;
    setBusy(true);
    try {
      const next = await revokeOrderShareLink(orderId);
      setRawToken(null);
      setStatus(next);
      toast.success("Ссылка отозвана");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!publicUrl) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(publicUrl);
      else {
        const ta = document.createElement("textarea");
        ta.value = publicUrl; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Ссылка скопирована");
    } catch (e) { toast.error("Не удалось скопировать"); }
  };

  const openAsClient = () => {
    if (!publicUrl) return;
    window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  // Loading — render nothing to avoid a flash.
  if (status === null) return null;

  // Read-only viewers see nothing at all — this is an owner-only surface.
  if (!canWrite && !status.active) return null;

  return (
    <div className="mt-3 rounded-lg p-3"
         style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-finished-share-link">
      <div className="flex items-center gap-1.5 mb-2 text-[10.5px] tracking-[0.14em] uppercase"
           style={{ color: "#8B6F4B" }}>
        <Link2 size={12} />
        Ссылка для клиента
      </div>

      {/* NOT CREATED YET */}
      {!status.active && canWrite && (
        <button onClick={create}
                disabled={busy}
                className="h-9 px-3.5 rounded-xl text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="mo-finished-share-link-create">
          {busy ? "Создаём…" : "Создать ссылку для клиента"}
        </button>
      )}

      {/* ACTIVE + raw token available in memory */}
      {status.active && publicUrl && (
        <>
          <div className="text-[12px] break-all mb-2 tabular-nums"
               style={{ color: "#2A1D14" }}
               data-testid="mo-finished-share-link-url">
            {publicUrl}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={copy}
                    className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                    style={{ background: "#FFFFFF", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-finished-share-link-copy">
              <Copy size={12} />
              Скопировать
            </button>
            <button onClick={openAsClient}
                    className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                    style={{ background: "#FFFFFF", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-finished-share-link-open">
              <ExternalLink size={12} />
              Открыть как клиент
            </button>
            {canWrite && (
              <button onClick={rotate}
                      disabled={busy}
                      className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#4A3728",
                               boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-finished-share-link-rotate">
                <RefreshCw size={12} />
                {busy ? "Обновляем…" : "Перевыпустить ссылку"}
              </button>
            )}
            {canWrite && (
              <button onClick={revoke}
                      disabled={busy}
                      className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#9A4637",
                               boxShadow: "inset 0 0 0 1px #E7CFC7" }}
                      data-testid="mo-finished-share-link-revoke">
                <X size={12} />
                Отозвать ссылку
              </button>
            )}
          </div>
        </>
      )}

      {/* ACTIVE but token not in memory (page was refreshed after creation).
          Backend cannot return the raw token again; we show a small hint and
          let the owner rotate to get a fresh URL. Old link keeps working
          until the rotate call revokes it (server-atomic). */}
      {status.active && !publicUrl && (
        <>
          <div className="text-[12px] mb-2" style={{ color: "#5B4326" }}
               data-testid="mo-finished-share-link-active-hint">
            Активная ссылка · оканчивается на ***{status.token_last4}
          </div>
          <div className="text-[11.5px] mb-2" style={{ color: "#8B6F4B" }}>
            Полный URL показывается только один раз при создании. Перевыпустите ссылку, чтобы получить его снова.
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-1.5">
              <button onClick={rotate}
                      disabled={busy}
                      className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#4A3728",
                               boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-finished-share-link-rotate">
                <RefreshCw size={12} />
                {busy ? "Обновляем…" : "Перевыпустить ссылку"}
              </button>
              <button onClick={revoke}
                      disabled={busy}
                      className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1 disabled:opacity-60"
                      style={{ background: "#FFFFFF", color: "#9A4637",
                               boxShadow: "inset 0 0 0 1px #E7CFC7" }}
                      data-testid="mo-finished-share-link-revoke">
                <X size={12} />
                Отозвать ссылку
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
